import { Table as TanstackTable } from '@tanstack/react-table';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { TableData, RowActionRenderer, getTableMeta } from './types';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	getPinnedColumnInfo,
	getPinnedColumnStyle,
	LEADING_TRACK_WIDTH,
} from './utils/pinnedColumn';

interface Props<T extends TableData> {
	table: TanstackTable<T>;
	enableRowDragAndDrop: boolean;
	enableRowSelection: boolean;
	showRowSelection: boolean;
	pinSelectionColumn: boolean;
	enableRowActions: boolean;
	renderRowActions?: RowActionRenderer<T>;
	renderRowActionMenuItems?: RowActionRenderer<T>;
	hideFooterRows: boolean;
	enableStickyFooter: boolean;
	enableVerticalCellBorders?: boolean;
	footerCellMinHeight?: string;
	footerCellAlignTop?: boolean;
}

export const MantineTableFooter = <T extends TableData>({
	table,
	enableRowDragAndDrop,
	enableRowSelection,
	showRowSelection,
	pinSelectionColumn,
	enableRowActions,
	renderRowActions,
	renderRowActionMenuItems,
	hideFooterRows,
	enableStickyFooter,
	enableVerticalCellBorders = false,
	footerCellMinHeight = '48px',
	footerCellAlignTop = false,
}: Props<T>) => {
	// Check if any columns have footers (shared between body and footer)
	const hasFooters = table
		.getVisibleLeafColumns()
		.some((column) => (column.columnDef as { footer?: unknown }).footer);

	if (hideFooterRows || !hasFooters) {
		return null;
	}

	const { pinnedColumns, lastPinnedColumnId } = getTableMeta(table);
	const isPinnedMode = !!pinnedColumns && Object.keys(pinnedColumns).length > 0;
	// In pinned mode the vertical stickiness lives on the full-width wrapper so
	// cells get a full-width containing block for sticky-left.
	const stickyOnCell = enableStickyFooter && !isPinnedMode;
	let gridColumnIndex = 1;

	const footerCells = (
		<>
			{enableRowDragAndDrop && (
				<div
					key="footer-drag-handle"
					style={{
						gridColumn: gridColumnIndex++,
						padding: '8px',
						height: '48px',
						borderTop: `1px solid ${defaultTheme.v3.colors.dark[0]}`,
						...(stickyOnCell && {
							position: 'sticky',
							bottom: 0,
							zIndex: Z_INDEX.STICKY_HEADER,
							backgroundColor: defaultTheme.v3.colors.white[0],
						}),
					}}
				/>
			)}
			{enableRowSelection && showRowSelection && (
				<div
					key="footer-select"
					style={{
						gridColumn: gridColumnIndex++,
						padding: '8px',
						height: '48px',
						borderTop: `1px solid ${defaultTheme.v3.colors.dark[0]}`,
						...(stickyOnCell && {
							position: 'sticky',
							bottom: 0,
							zIndex: Z_INDEX.STICKY_HEADER,
							backgroundColor: defaultTheme.v3.colors.white[0],
						}),
						...(pinSelectionColumn &&
							getPinnedColumnStyle(true, Z_INDEX.STICKY_PINNED_COLUMN, {
								backgroundColor: defaultTheme.v3.colors.white[0],
								left: enableRowDragAndDrop ? LEADING_TRACK_WIDTH : 0,
								withShadow: false,
							})),
					}}
				/>
			)}
			{table.getVisibleLeafColumns().map((column, index, columns) => {
				const footer = (column.columnDef as { footer?: unknown }).footer;
				const currentGridColumn = gridColumnIndex++;
				const isLastColumn = index === columns.length - 1;
				const pin = getPinnedColumnInfo(pinnedColumns, lastPinnedColumnId, column.id);

				return (
					<div
						key={`${column.id}-footer`}
						style={{
							gridColumn: currentGridColumn,
							padding: '8px',
							minHeight: footerCellMinHeight,
							height: 'auto',
							backgroundColor: defaultTheme.v3.colors.white[0],
							borderTop: `1px solid ${defaultTheme.v3.colors.dark[0]}`,
							display: 'flex',
							alignItems: footerCellAlignTop ? 'flex-start' : 'center',
							...(enableVerticalCellBorders &&
								!isLastColumn && {
									borderRight: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								}),
							...(stickyOnCell && {
								position: 'sticky',
								bottom: 0,
								zIndex: Z_INDEX.STICKY_HEADER,
							}),
							...getPinnedColumnStyle(pin.isPinned, Z_INDEX.STICKY_PINNED_HEADER, {
								backgroundColor: defaultTheme.v3.colors.white[0],
								left: pin.left,
								withShadow: pin.isLast,
							}),
						}}
					>
						{typeof footer === 'function' ? footer() : footer}
					</div>
				);
			})}
			{enableRowActions && (renderRowActions || renderRowActionMenuItems) ? (
				<div
					key="footer-actions"
					style={{
						gridColumn: gridColumnIndex,
						padding: '8px',
						height: '48px',
						backgroundColor: defaultTheme.v3.colors.white[0],
						borderTop: `1px solid ${defaultTheme.v3.colors.dark[0]}`,
						...(stickyOnCell && {
							position: 'sticky',
							bottom: 0,
							zIndex: Z_INDEX.STICKY_HEADER,
						}),
					}}
				/>
			) : null}
		</>
	);

	if (isPinnedMode) {
		return (
			<div
				data-table-footer
				style={{
					gridColumn: '1 / -1',
					display: 'grid',
					gridTemplateColumns: getTableMeta(table).gridLayout?.gridTemplateColumns,
					backgroundColor: defaultTheme.v3.colors.white[0],
					...(enableStickyFooter && {
						position: 'sticky',
						bottom: 0,
						zIndex: Z_INDEX.STICKY_HEADER,
					}),
				}}
			>
				{footerCells}
			</div>
		);
	}

	return footerCells;
};
